'use client';

/**
 * @file Defines the journal store using Zustand for managing journal entry state.
 */

import { JournalImageInput, JournalType } from '@/__generated__/API';
import { idbStorage } from '@/stores/idb-storage';
import { MessageDescriptor } from '@lingui/core';
import assign from 'lodash-es/assign';
import { createStore } from 'zustand-x';
import { createJSONStorage } from 'zustand/middleware';

/** Represents the structure for creating a new journal entry. */
export type JournalStoreCreateItem = {
  type: JournalType;
  customPrompts: MessageDescriptor[];
  images: JournalImageInput[];
  responses: string[];
};

/** Represents the structure for updating an existing journal entry. */
export type JournalStoreUpdateItem = {
  responses: string[];
};

/** Represents the overall structure of the journal store. */
export type JournalStore = {
  update: Record<string, JournalStoreUpdateItem>;
  create: Record<string, JournalStoreCreateItem>;
};

export const DefaultJournalParent = 'DEFAULT';
const getDefaultCreateItem = (): JournalStoreCreateItem => ({
  type: JournalType.CUSTOM,
  images: [],
  responses: [],
  customPrompts: []
});
/** The initial value for the journal store. */
const initialValue: JournalStore = {
  // Create state is kept separate for different parent IDs
  create: {
    [DefaultJournalParent]: getDefaultCreateItem()
  },
  update: {}
};

/**
 * Zustand store for managing journal entries and their states.
 * Includes methods for setting responses, updating data, and resetting the store.
 */
export const journalStore = createStore('journal')<JournalStore>(initialValue, {
  persist: {
    enabled: true,
    storage: createJSONStorage(() => idbStorage)
  }
})
  .extendSelectors((state, get) => ({
    getCreateByParentId(parentId = DefaultJournalParent) {
      return get.create()[parentId] || getDefaultCreateItem();
    }
  }))
  .extendActions((set) => ({
    /**
     * Sets a response in the create object at the specified index.
     * @param index - The index of the response to set.
     * @param response - The response string to set.
     * @param [parentId] - parent ID for journal entry
     */
    setCreateIndexedResponse(
      index: number,
      response: string,
      parentId = DefaultJournalParent
    ) {
      set.state((draft) => {
        if (!draft.create[parentId]) {
          draft.create[parentId] = getDefaultCreateItem();
        }
        draft.create[parentId].responses[index] = response;
      });
    },

    /**
     * Sets partial data in the create object.
     * @param input - The partial data to assign to the create object.
     * @param [parentId] - parent ID for journal entry
     */
    setCreateData(
      input: Partial<JournalStoreCreateItem>,
      parentId = DefaultJournalParent
    ) {
      set.state((draft) => {
        if (!draft.create[parentId]) {
          draft.create[parentId] = getDefaultCreateItem();
        }
        assign(draft.create[parentId], input);
      });
    },
    /**
     * Sets a response in the update object for the specified ID at the specified index.
     * @param id - The ID of the update object.
     * @param index - The index of the response to set.
     * @param response - The response string to set.
     */
    setUpdateIndexedResponse(id: string, index: number, response: string) {
      set.state((draft) => {
        if (!draft.update[id]) {
          draft.update[id] = { responses: [] };
        }
        draft.update[id].responses[index] = response;
      });
    },

    /**
     * Sets the responses array for the specified ID in the update object.
     * @param id - The ID of the update object.
     * @param responses - The responses array to set.
     */
    setUpdateResponses(id: string, responses: string[]) {
      set.state((draft) => {
        draft.update[id] = { responses };
      });
    },

    /**
     * Resets the responses for the specified ID in the update object.
     * @param id - The ID of the update object to reset.
     */
    resetUpdateResponses(id: string) {
      set.state((draft) => {
        delete draft.update[id];
      });
    },

    /**
     * Resets the entire journal store to its initial value.
     */
    reset() {
      set.state((draft) => {
        draft.create = initialValue.create;
        draft.update = initialValue.update;
      });
    }
  }));
